import React from 'react'
import { observer } from 'mobx-react'
import CloseJobAsAdminDialogViewModel, {
  ViewResolution,
} from './CloseJobAsAdminViewModel'
import JobViewModel from 'jobs/views/JobRoot/Job/JobViewModel'
import {
  ModalSheet,
  ModalSheetContent,
  ModalSheetContentBlock,
  ModalSheetContentDescription,
  ModalSheetContentIllustration,
  ModalSheetContentTitle,
  ModalSheetFooter,
  ModalSheetFooterButtonGroup,
  ModalSheetFooterSpacer,
  ModalSheetHeader,
} from 'components/ModalSheet'
import { useBrandingSettings } from 'hooks/branding'
import {
  CustomSelect,
  useCustomSelectStateFromItemSource,
} from 'components/inputs/CustomSelect'
import { match } from 'utils/pattern-match-util'
import { Resolution } from 'jobs/Job'
import {
  CheckboxInputContainer,
  FormRow,
  SelectContainer,
  TextInputContainer,
} from 'components/inputs'
import ErrorBlock from 'components/ErrorBlock'
import Button from 'components/Button'
import {
  NotificationBar,
  NotificationBarSubtitle,
  NotificationBarTitle,
} from 'components/NotificationBar'

interface Props {
  vm: CloseJobAsAdminDialogViewModel
  jobVM: JobViewModel
}

export default observer(function CloseJobAsAdminDialog({ vm, jobVM }: Props) {
  const { corporateMode } = useBrandingSettings()

  const billingMethodSelectProps = useCustomSelectStateFromItemSource({
    itemSource: vm.fetchBillingMethods,
  })

  /**
   * Maps the domain `Resolution` enumeration to a View format.
   */
  const resolutionItems = React.useMemo(
    () =>
      vm.resolutionItems.map((i) =>
        match<Resolution, [Resolution, ViewResolution]>(i, {
          [Resolution.Done]: () => [Resolution.Done, 'Done'],
          [Resolution.Cancelled]: () => [Resolution.Cancelled, 'Cancelled'],
          [Resolution.Test]: () => [Resolution.Test, 'Test'],
        }),
      ),
    [vm.resolutionItems],
  )

  return (
    <ModalSheet showing={vm.showing} onRequestClose={vm.close}>
      <ModalSheetHeader withCloseButton />
      <ModalSheetContent>
        <ModalSheetContentBlock>
          {!corporateMode && (
            <ModalSheetContentIllustration
              src={require('assets/images/img_job-rating.svg')}
              alt="Illustration of a finger tapping a star rating on a phone"
            />
          )}
          <ModalSheetContentTitle>Complete Job</ModalSheetContentTitle>
          <ModalSheetContentDescription>
            By marking this <strong>{vm.jobName}</strong> as completed,
            communication with the client will cease.
          </ModalSheetContentDescription>
        </ModalSheetContentBlock>
        <ModalSheetContentBlock>
          <FormRow>
            <SelectContainer
              label="Resolution"
              model={vm}
              field="resolution"
              items={resolutionItems}
              help="The resolution for this job."
              disabled={jobVM.isScopeChangeRequestPending}
            />
          </FormRow>

          <FormRow>
            <TextInputContainer
              label="Reason"
              model={vm}
              field="reason"
              help="Why are you closing this job? This text is public and can be seen by the Client and the Pros."
              disabled={jobVM.isScopeChangeRequestPending}
            />
          </FormRow>

          {vm.billingAllowed && (
            <FormRow>
              <CheckboxInputContainer
                model={vm}
                field="shouldBillJob"
                label={vm.billingOperationMessage}
                disabled={jobVM.isScopeChangeRequestPending}
              />
            </FormRow>
          )}

          {vm.shouldCollectBillingMethod && (
            <FormRow>
              <CustomSelect
                searchResultsPlacement="above"
                multi={false}
                {...billingMethodSelectProps}
                value={vm.selectedBillingMethod}
                onChange={vm.setBillingMethod}
                itemToString={(i) => i.displayName}
                disabled={jobVM.isScopeChangeRequestPending}
              />
            </FormRow>
          )}
        </ModalSheetContentBlock>
      </ModalSheetContent>
      {vm.closeJob.rejected && (
        <ErrorBlock
          tackOn="bottom"
          error={vm.closeJob.error}
          onCloseClick={() => vm.closeJob.reset()}
        />
      )}
      <PendingUpdatesNotificationBar vm={jobVM} />
      <ModalSheetFooter>
        <ModalSheetFooterSpacer />
        <ModalSheetFooterButtonGroup>
          <Button onClick={vm.close} disabled={vm.closeJob.pending}>
            Cancel
          </Button>
          <Button
            type="primary"
            onClick={vm.closeJob}
            data-test="close-job-as-admin-submit-button"
            loading={vm.closeJob.pending}
            disabled={jobVM.isScopeChangeRequestPending}
          >
            Close Job
          </Button>
        </ModalSheetFooterButtonGroup>
      </ModalSheetFooter>
    </ModalSheet>
  )
})

interface PendingUpdatesNotificationBarProps {
  vm: JobViewModel
}

const PendingUpdatesNotificationBar = observer(
  ({ vm }: PendingUpdatesNotificationBarProps) => {
    if (!vm.isScopeChangeRequestPending) {
      return
    }

    return (
      <NotificationBar centered type="error">
        <NotificationBarTitle>Pending updates:</NotificationBarTitle>
        <NotificationBarSubtitle>
          Unable to close due to pending job scope changes
        </NotificationBarSubtitle>
      </NotificationBar>
    )
  },
)
